import { Button, Space } from 'antd';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { updateCourseEnrolment } from '../../services/courseEnrolmentService';
import DeleteWarningModal from '../Common/DeleteWarningModal';
import TableWithSorter from '../Common/TableWithSorter';
import CourseEnrolmentModal from '../courseEnrolment/CourseEnrolmentModal';
import EnrolmentRefundModal from './RefundEnrolment/EnrolmentRefundModal';

const CourseEnrolmentTable = ({
  data,
  setSelectedRowKeys,
  setSelectedRows,
}) => {
  const [formData, setFormData] = useState({
    courseEnrolmentId: '',
    courseOfferId: '',
    courseEnrolmentStatus: '',
    courseOffer: { code: '' },
    learnerId: '',
    firstName: '',
    lastName: '',
  });
  const [refundFormData, setRefundFormData] = useState({
    isReasonCustom: false,
    courseEnrolmentId: '',
    reasonType: 'PERSONAL',
    isPartial: true,
    localAmount: 0,
    localCurrency: 'USD',
    currency: 'USD',
    amount : 0
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [status, setStatus] = useState({
    error: false,
    message: '',
  });

  const handleDelete = (record) => {
    setFormData({
      courseEnrolmentId: record.courseEnrolmentId,
      courseOfferId: record.courseOfferId,
      courseEnrolmentStatus: record.courseEnrolmentStatus,
      courseOfferCode: record.code,
      learnerId: record.learnerId,
    });
    setIsDeleteModalVisible(true);
  };

  const handleRefund = (record) => {
    setRefundFormData({
      isReasonCustom: false,
      courseEnrolmentId: record.courseEnrolmentId,
      reasonType: 'PERSONAL',
      isPartial: true,
      localAmount: 0,
      localCurrency: 'USD',
      currency: 'USD',
      amount : 0
    });
    setIsRefundModalVisible(true);
  };

  const handleEdit = (record) => {
    setFormData({
      courseEnrolmentId: record.courseEnrolmentId,
      courseOfferId: record.courseOfferId,
      courseEnrolmentStatus: record.courseEnrolmentStatus,
      courseOfferCode: record.courseOffer.code,
      learnerId: record.learnerId,
      firstName: record.learner.firstName,
      lastName: record.learner.lastName,
    });
    setIsEditModalVisible(true);
  };

  const updateCourseEnrolmentHandler = () => {
    const body = formData;
    updateCourseEnrolment(formData.courseEnrolmentId, body)
      .then(() => {
        setIsEditModalVisible(false);
      })
      .catch((err) => {
        setStatus({
          error: true,
          message: `${err}`,
        });
      });
  };

  const courseEnrolmentStatusArray = [
    'Inactive',
    'Withdrawn',
    'Transferred',
    'Cancelled',
  ];

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
      console.log(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: courseEnrolmentStatusArray.includes(
        record.courseEnrolmentStatus
      ), // Column configuration not to be checked
      _id: record._id,
    }),
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      sorter: (a, b) => a._id - b._id,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'EnrolmentId',
      dataIndex: 'courseEnrolmentId',
      key: 'courseEnrolmentId',
      sorter: (a, b) => a.courseEnrolmentId - b.courseEnrolmentId,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Course Offer',
      dataIndex: 'courseOffer',
      key: 'courseOffer',
      render: (value) => <Space size="middle">{value?.code}</Space>,
    },
    {
      title: 'Course Offer Id',
      dataIndex: 'courseOffer',
      key: 'courseOffer',
      render: (value) => (
        <Space size="middle">
          {value?.courseOfferId ? (
            <Link to={`/course-offer/${value.courseOfferId}`}>
              {value.courseOfferId}
            </Link>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'FirstName',
      dataIndex: ['learner', 'firstName'],
      key: ['learner', 'firstName'],
      sorter: (a, b) => a.learner.firstName.localeCompare(b.learner.firstName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'LastName',
      dataIndex: ['learner', 'lastName'],
      key: ['learner', 'lastName'],
      sorter: (a, b) => a.learner.lastName.localeCompare(b.learner.lastName),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Email',
      dataIndex: ['learner', 'email'],
      key: ['learner', 'email'],
      sorter: (a, b) => a.learner.email.localeCompare(b.learner.email),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'LearnerId',
      dataIndex: 'learnerId',
      key: 'learnerId',
      sorter: (a, b) => a.learnerId - b.learnerId,
      sortDirections: ['descend', 'ascend'],
      render: (text, record) => (
        <Space size="middle">
          <Link to={'/learner/' + record.learnerId}>{text}</Link>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'courseEnrolmentStatus',
      key: 'courseEnrolmentStatus',
      filters: [
        {
          text: 'Pending',
          value: 'Pending',
        },
        {
          text: 'Current',
          value: 'Current',
        },
        {
          text: 'Inactive',
          value: 'Inactive',
        },
        {
          text: 'Completed',
          value: 'Completed',
        },
        {
          text: 'Transferred',
          value: 'Transferred',
        },
        {
          text: 'Withdrawn',
          value: 'Withdrawn',
        },
        {
          text: 'Cancelled',
          value: 'Cancelled',
        },
      ],
      // specify the condition of filtering result
      // here is that finding the name started with `value`
      onFilter: (value, record) =>
        record.courseEnrolmentStatus.indexOf(value) === 0,
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button onClick={() => handleDelete(record)}>Delete</Button>
          <Button onClick={() => handleRefund(record)}>Refund</Button>
        </Space>
      ),
    },
  ];
  return (
    <>
      <TableWithSorter
        rowKey="_id"
        rowSelection={{
          ...rowSelection,
        }}
        tableLayout="auto"
        scroll={{ x: 1000 }}
        expandable={{ defaultExpandAllRows: true }}
        dataSource={data}
        columns={columns}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '50', '500', '1000'],
        }}
      />
      <DeleteWarningModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        deleteLink={''}
        params={{}}
      />

      {isRefundModalVisible ? (
        <EnrolmentRefundModal
          isVisible={isRefundModalVisible}
          setIsVisible={setIsRefundModalVisible}
          initialValues={refundFormData}
        />
      ) : (
        <CourseEnrolmentModal
          isVisible={isEditModalVisible}
          setIsVisible={setIsEditModalVisible}
          handleOk={updateCourseEnrolmentHandler}
          formData={formData}
          updateFormData={setFormData}
          title={'Editing Course Enrolment ' + formData.courseEnrolmentId}
        />
      )}
    </>
  );
};

export default CourseEnrolmentTable;
